import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { SEO, getServiceSchema, getFAQSchema } from '../../components/SEO';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../../components/ui/accordion";
import { 
  Users, CheckCircle, ArrowRight, Phone, 
  Heart, Shield, Clock, BookOpen 
} from 'lucide-react';

export const ParentingSupportPage = () => {
  const navigate = useNavigate();

  const faqs = [
    {
      question: "How long does parenting support therapy typically last?",
      answer: "The duration varies based on your specific needs and goals. Some parents find relief in 6-8 sessions, while others benefit from ongoing support over several months. We'll discuss your goals during the initial consultation and create a plan that works for your family."
    },
    {
      question: "Do my children need to attend the parenting support sessions?",
      answer: "It depends on your goals. Many sessions are parent-only, where we focus on developing strategies and skills. However, we may include family sessions when appropriate to practice new communication techniques or address specific family dynamics together."
    },
    {
      question: "Is parenting support therapy available via telehealth in Hawaii?",
      answer: "Yes! I offer secure telehealth sessions throughout Hawaii, making it convenient for busy parents to receive support from home. Telehealth is especially helpful for parents juggling multiple responsibilities or those living outside the Wailuku area."
    },
    {
      question: "What age children do you work with in parenting support?",
      answer: "I work with parents of children of all ages—from toddlers to teenagers. Each developmental stage comes with unique challenges, and I tailor my approach to address the specific issues you're facing with your child's age group."
    },
    {
      question: "Will you teach me specific parenting techniques I can use at home?",
      answer: "Absolutely. My approach is very practical and skill-focused. You'll learn specific, evidence-based techniques for communication, discipline, and emotional regulation that you can implement immediately at home. I also provide resources and strategies tailored to your family's needs."
    },
    {
      question: "Does seeking parenting support mean I'm a bad parent?",
      answer: "Not at all—quite the opposite! Seeking support shows you're a committed parent who wants the best for your family. Parenting is one of the hardest jobs in the world, and asking for help is a sign of strength and wisdom. Every parent faces challenges, and having professional guidance can make all the difference."
    }
  ];

  const serviceSchema = getServiceSchema(
    "Parenting Support Therapy",
    "Professional parenting support and counseling in Hawaii. Expert guidance for parents facing challenges with children of all ages, from toddlers to teenagers.",
    "https://aloha-parents.preview.emergentagent.com/services/parenting-support"
  );

  const faqSchema = getFAQSchema(faqs);

  const combinedSchema = {
    "@context": "https://schema.org",
    "@graph": [serviceSchema, faqSchema]
  };

  const benefits = [
    "Develop effective communication strategies with your children",
    "Learn positive discipline techniques that work",
    "Reduce parenting stress and overwhelm",
    "Build stronger parent-child bonds",
    "Navigate age-specific challenges with confidence",
    "Create a peaceful, harmonious home environment"
  ];

  const whoItsFor = [
    "Parents feeling overwhelmed by daily parenting challenges",
    "Families experiencing behavioral issues with children",
    "Parents struggling with work-life balance",
    "New parents adjusting to parenthood",
    "Parents of teens navigating adolescent challenges",
    "Co-parents working on unified parenting approaches"
  ];

  const whatToExpect = [
    {
      title: "Initial Assessment",
      description: "We'll discuss your specific challenges, family dynamics, and parenting goals in a safe, non-judgmental environment."
    },
    {
      title: "Personalized Strategy",
      description: "Together, we'll develop practical strategies tailored to your family's unique needs and parenting style."
    },
    {
      title: "Skill Building",
      description: "Learn evidence-based techniques for communication, discipline, and emotional regulation."
    },
    {
      title: "Ongoing Support",
      description: "Regular sessions to refine approaches, celebrate successes, and address new challenges as they arise."
    }
  ];

  return (
    <div className="min-h-screen bg-white pt-24 pb-20">
      <SEO 
        title="Parenting Support Therapy in Hawaii"
        description="Expert parenting support and counseling in Wailuku, HI. Licensed therapist helping parents navigate challenges with children of all ages. Telehealth available throughout Hawaii."
        keywords="parenting support Hawaii, parenting therapy Wailuku, parenting counseling Maui, family parenting help Hawaii, parent coaching Hawaii"
        canonicalUrl="https://aloha-parents.preview.emergentagent.com/services/parenting-support"
        schemaData={combinedSchema}
      />
      
      {/* Breadcrumb */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-8">
        <div className="flex items-center text-sm text-gray-600">
          <button onClick={() => navigate('/')} className="hover:text-teal-600 transition-colors">
            Home
          </button>
          <ArrowRight className="w-4 h-4 mx-2" />
          <button onClick={() => navigate('/#services')} className="hover:text-teal-600 transition-colors">
            Services
          </button>
          <ArrowRight className="w-4 h-4 mx-2" />
          <span className="text-gray-900 font-medium">Parenting Support</span>
        </div>
      </div>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-teal-50 via-blue-50 to-white py-16 px-4 sm:px-6 lg:px-8 mb-16">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="w-16 h-16 bg-teal-100 rounded-2xl flex items-center justify-center mb-6">
                <Users className="w-8 h-8 text-teal-600" />
              </div>
              <Badge className="mb-4 bg-teal-100 text-teal-800">Professional Parenting Support</Badge>
              <h1 className="text-5xl font-bold text-gray-900 mb-6">
                Parenting Support in Hawaii
              </h1>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                Parenting is one of life's most rewarding experiences, but it can also be challenging. 
                Whether you're dealing with behavioral issues, communication struggles, or simply feeling 
                overwhelmed, professional support can make all the difference.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  size="lg"
                  onClick={() => navigate('/contact')}
                  className="bg-teal-600 hover:bg-teal-700 text-white px-8 py-6 text-lg transition-all duration-200"
                >
                  <Phone className="w-5 h-5 mr-2" />
                  Schedule Consultation
                </Button>
                <Button 
                  size="lg"
                  variant="outline"
                  onClick={() => window.location.href = 'tel:8084707650'}
                  className="border-2 border-teal-600 text-teal-600 hover:bg-teal-50 px-8 py-6 text-lg transition-all duration-200"
                >
                  Call (808) 470-7650
                </Button>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1476703993599-0035a21b17a9?w=800&q=80" 
                alt="Parent and child bonding"
                className="rounded-2xl shadow-2xl w-full"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Overview Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Navigate Parenting Challenges with Expert Guidance
            </h2>
            <div className="prose prose-lg max-w-none text-gray-700 space-y-4">
              <p>
                Every parent faces challenges, and seeking support is a sign of strength, not weakness. 
                My parenting support services provide you with the tools, strategies, and confidence you 
                need to navigate the complexities of raising children in today's world.
              </p>
              <p>
                With over 11 years of experience in education and extensive training in family therapy, 
                I understand the unique pressures parents face. From toddler tantrums to teenage rebellion, 
                from sibling rivalry to screen time battles, I'm here to help you develop effective 
                strategies that work for your family.
              </p>
              <p>
                My approach is collaborative and non-judgmental. We'll work together to identify what's 
                working, address what isn't, and build on your strengths as a parent. The goal is to 
                create a more peaceful, connected family dynamic where everyone thrives.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Who It's For Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-blue-100 text-blue-800">Is This For You?</Badge>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Who Can Benefit from Parenting Support
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              This service is designed for parents at any stage of the parenting journey
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {whoItsFor.map((item, index) => (
              <Card key={index} className="border-none shadow-lg bg-white hover:shadow-xl transition-all duration-300">
                <CardContent className="pt-6">
                  <div className="flex items-start">
                    <CheckCircle className="w-6 h-6 text-teal-600 mr-3 mt-1 flex-shrink-0" />
                    <p className="text-gray-700 leading-relaxed">{item}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* What to Expect Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-purple-100 text-purple-800">The Process</Badge>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              What to Expect in Parenting Support Therapy
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {whatToExpect.map((item, index) => (
              <Card key={index} className="border-none shadow-lg bg-gradient-to-br from-white to-teal-50 hover:shadow-xl transition-all duration-300">
                <CardHeader>
                  <div className="w-12 h-12 bg-teal-600 text-white rounded-xl flex items-center justify-center mb-4 font-bold text-xl">
                    {index + 1}
                  </div>
                  <CardTitle className="text-2xl">{item.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-700 text-base leading-relaxed">{item.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="mb-4 bg-green-100 text-green-800">Benefits</Badge>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Transform Your Parenting Journey
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                Working with a therapist specialized in parenting support can help you:
              </p>
              <div className="space-y-4">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-start">
                    <CheckCircle className="w-6 h-6 text-teal-600 mr-4 mt-1 flex-shrink-0" />
                    <p className="text-gray-700 text-lg leading-relaxed">{benefit}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-6">
              <Card className="border-none shadow-lg bg-white text-center">
                <CardContent className="pt-6">
                  <Heart className="w-12 h-12 text-red-500 mx-auto mb-4" />
                  <h3 className="font-bold text-2xl text-gray-900 mb-2">Stronger Bonds</h3>
                  <p className="text-gray-600">Build deeper connections with your children</p>
                </CardContent>
              </Card>
              <Card className="border-none shadow-lg bg-white text-center">
                <CardContent className="pt-6">
                  <Shield className="w-12 h-12 text-blue-500 mx-auto mb-4" />
                  <h3 className="font-bold text-2xl text-gray-900 mb-2">Less Stress</h3>
                  <p className="text-gray-600">Reduce daily parenting overwhelm</p>
                </CardContent>
              </Card>
              <Card className="border-none shadow-lg bg-white text-center">
                <CardContent className="pt-6">
                  <BookOpen className="w-12 h-12 text-purple-500 mx-auto mb-4" />
                  <h3 className="font-bold text-2xl text-gray-900 mb-2">New Skills</h3>
                  <p className="text-gray-600">Learn effective parenting techniques</p>
                </CardContent>
              </Card>
              <Card className="border-none shadow-lg bg-white text-center">
                <CardContent className="pt-6">
                  <Clock className="w-12 h-12 text-amber-500 mx-auto mb-4" />
                  <h3 className="font-bold text-2xl text-gray-900 mb-2">Long-term Success</h3>
                  <p className="text-gray-600">Create lasting positive change</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Therapeutic Approaches */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-indigo-100 text-indigo-800">Evidence-Based Methods</Badge>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Therapeutic Approaches Used
            </h2>
          </div>

          <div className="space-y-6">
            <Card className="border-none shadow-lg">
              <CardHeader>
                <CardTitle className="text-2xl">Cognitive Behavioral Therapy (CBT)</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700 leading-relaxed">
                  Learn to identify and change unhelpful thought patterns and behaviors. CBT helps 
                  parents develop practical strategies for managing stress and responding to challenging 
                  situations more effectively.
                </p>
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg">
              <CardHeader>
                <CardTitle className="text-2xl">Family Systems Therapy</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700 leading-relaxed">
                  Understand how family dynamics and relationships influence behavior. This approach 
                  helps identify patterns and create healthier interactions within the entire family system.
                </p>
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg">
              <CardHeader>
                <CardTitle className="text-2xl">Positive Parenting Techniques</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700 leading-relaxed">
                  Focus on building positive relationships through encouragement, setting clear 
                  boundaries, and teaching problem-solving skills. This approach emphasizes mutual 
                  respect and effective communication.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-amber-100 text-amber-800">Common Questions</Badge>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Frequently Asked Questions About Parenting Support
            </h2>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            <AccordionItem 
              value="item-1"
              className="bg-gray-50 border-none shadow-md rounded-lg px-6"
            >
              <AccordionTrigger className="text-left text-lg font-semibold text-gray-900 hover:text-teal-600 transition-colors">
                How long does parenting support therapy typically last?
              </AccordionTrigger>
              <AccordionContent className="text-gray-600 text-base leading-relaxed pt-2">
                The duration varies based on your specific needs and goals. Some parents find relief in 6-8 sessions, 
                while others benefit from ongoing support over several months. We'll discuss your goals during the 
                initial consultation and create a plan that works for your family.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem 
              value="item-2"
              className="bg-gray-50 border-none shadow-md rounded-lg px-6"
            >
              <AccordionTrigger className="text-left text-lg font-semibold text-gray-900 hover:text-teal-600 transition-colors">
                Do my children need to attend the parenting support sessions?
              </AccordionTrigger>
              <AccordionContent className="text-gray-600 text-base leading-relaxed pt-2">
                It depends on your goals. Many sessions are parent-only, where we focus on developing strategies 
                and skills. However, we may include family sessions when appropriate to practice new communication 
                techniques or address specific family dynamics together.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem 
              value="item-3"
              className="bg-gray-50 border-none shadow-md rounded-lg px-6"
            >
              <AccordionTrigger className="text-left text-lg font-semibold text-gray-900 hover:text-teal-600 transition-colors">
                Is parenting support therapy available via telehealth in Hawaii?
              </AccordionTrigger>
              <AccordionContent className="text-gray-600 text-base leading-relaxed pt-2">
                Yes! I offer secure telehealth sessions throughout Hawaii, making it convenient for busy parents 
                to receive support from home. Telehealth is especially helpful for parents juggling multiple 
                responsibilities or those living outside the Wailuku area.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem 
              value="item-4"
              className="bg-gray-50 border-none shadow-md rounded-lg px-6"
            >
              <AccordionTrigger className="text-left text-lg font-semibold text-gray-900 hover:text-teal-600 transition-colors">
                What age children do you work with in parenting support?
              </AccordionTrigger>
              <AccordionContent className="text-gray-600 text-base leading-relaxed pt-2">
                I work with parents of children of all ages—from toddlers to teenagers. Each developmental stage 
                comes with unique challenges, and I tailor my approach to address the specific issues you're facing 
                with your child's age group.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem 
              value="item-5"
              className="bg-gray-50 border-none shadow-md rounded-lg px-6"
            >
              <AccordionTrigger className="text-left text-lg font-semibold text-gray-900 hover:text-teal-600 transition-colors">
                Will you teach me specific parenting techniques I can use at home?
              </AccordionTrigger>
              <AccordionContent className="text-gray-600 text-base leading-relaxed pt-2">
                Absolutely. My approach is very practical and skill-focused. You'll learn specific, evidence-based 
                techniques for communication, discipline, and emotional regulation that you can implement immediately 
                at home. I also provide resources and strategies tailored to your family's needs.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem 
              value="item-6"
              className="bg-gray-50 border-none shadow-md rounded-lg px-6"
            >
              <AccordionTrigger className="text-left text-lg font-semibold text-gray-900 hover:text-teal-600 transition-colors">
                Does seeking parenting support mean I'm a bad parent?
              </AccordionTrigger>
              <AccordionContent className="text-gray-600 text-base leading-relaxed pt-2">
                Not at all—quite the opposite! Seeking support shows you're a committed parent who wants the best 
                for your family. Parenting is one of the hardest jobs in the world, and asking for help is a sign 
                of strength and wisdom. Every parent faces challenges, and having professional guidance can make 
                all the difference.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-teal-600 to-blue-600 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Ready to Transform Your Parenting Experience?
          </h2>
          <p className="text-xl mb-8 opacity-95">
            Take the first step toward a more peaceful, connected family life. 
            Schedule your consultation today.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg"
              onClick={() => navigate('/contact')}
              className="bg-white text-teal-600 hover:bg-gray-100 px-8 py-6 text-lg font-semibold transition-all duration-200 shadow-xl"
            >
              Schedule Consultation
            </Button>
            <Button 
              size="lg"
              variant="outline"
              onClick={() => navigate('/resources')}
              className="border-2 border-white text-white hover:bg-white/10 px-8 py-6 text-lg font-semibold transition-all duration-200"
            >
              Read Parenting Resources
            </Button>
          </div>
          <p className="mt-6 text-sm opacity-80">
            Telehealth appointments available • Currently accepting new clients
          </p>
        </div>
      </section>
    </div>
  );
};
